import * as React from 'react';

import { ChevronRight, Folder, FolderOpen } from 'lucide-react';
import {
  Button,
  Tree as AriaTree,
  TreeItem as AriaTreeItem,
  TreeItemContent as AriaTreeItemContent,
  TreeItemRenderProps,
  type TreeItemContentRenderProps as AriaTreeItemContentRenderProps,
  composeRenderProps,
} from 'react-aria-components';

import { cn } from '../../../lib/stylingUtils';

/**
 * Tree component for hierarchical navigation.
 * Based on React Aria Tree component with proper keyboard navigation and accessibility.
 *
 * @example
 * ```tsx
 * <Tree aria-label="Files">
 *   <TreeItem id="documents" textValue="Documents">
 *     <TreeItemContent>
 *       <FolderIcon />
 *       Documents
 *     </TreeItemContent>
 *     <TreeItem id="file1" textValue="File 1">
 *       <TreeItemContent>
 *         <FileIcon />
 *         File 1
 *       </TreeItemContent>
 *     </TreeItem>
 *   </TreeItem>
 * </Tree>
 * ```
 */
const Tree = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof AriaTree> & {
  ref?: React.RefObject<HTMLDivElement>;
}) => (
  <AriaTree
    ref={ref}
    className={cn(
      '@Tree',
      cn(
        // Base tree styles
        'flex flex-col gap-0.5',
        'text-sm',
        'outline-none',
        // Focus ring
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
        className,
      ),
    )}
    {...props}
  />
);
Tree.displayName = 'Tree';

/**
 * Individual tree item component.
 * Can contain other tree items for nested structures.
 */
const TreeItem = ({
  className,
  children,
  ref,
  ...props
}: React.ComponentProps<typeof AriaTreeItem> & {
  ref?: React.RefObject<HTMLDivElement>;
}) => (
  <AriaTreeItem
    ref={ref}
    className={composeRenderProps(
      className as
        | string
        | ((renderProps: TreeItemRenderProps) => string)
        | undefined,
      (className, renderProps: TreeItemRenderProps) =>
        cn(
          '@TreeItem',
          'group outline-none',
          renderProps.isSelected && [
            'bg-primary text-abs-white',
            '[&_.tree-item-chevron]:text-abs-white',
          ],
          // Hover state
          'hover:bg-primary/50',
          // Focus state
          'data-[focus-visible]:outline-2 data-[focus-visible]:outline-offset-2 data-[focus-visible]:outline-primary',
          // Disabled state
          'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
          className,
        ),
    )}
    {...props}
  >
    {children}
  </AriaTreeItem>
);
TreeItem.displayName = 'TreeItem';

/**
 * Content wrapper for tree item.
 * Handles the visual content of the tree item.
 */
export type TreeItemContentProps = {
  children: React.ReactNode;
  className?: string;
  ref?: React.RefObject<HTMLDivElement>;
};

const TreeItemContent = ({
  className,
  children,
  ref,
}: TreeItemContentProps) => (
  <AriaTreeItemContent>
    {(_props: AriaTreeItemContentRenderProps) => (
      <div
        ref={ref}
        className={cn(
          // Layout
          'flex items-center gap-2 px-2 py-1',
          // Indentation handled by React Aria with CSS custom properties
          'pl-[calc(0.5rem+(var(--tree-item-level)-1)*0.8rem)]',
          // Interactive
          'select-none',
          // Transitions
          'transition-colors duration-200',
          className,
        )}
      >
        {/* Chevron button for expandable items */}
        <Button
          slot="chevron"
          className={cn(
            // Size and layout
            'flex size-4 items-center justify-center',
            // Visibility - only show for items with children
            'invisible group-data-[has-child-items]:visible',
            // Rotation for expanded state
            'transition-transform duration-200',
            'group-data-[expanded]:rotate-90',
            // Colors
            'text-foreground/40 hover:text-foreground',
            // Reset button styles
            'border-0 bg-transparent p-0 outline-none',
            // Focus
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
          )}
        >
          <ChevronRight className="size-3" />
        </Button>

        {/* Content area */}
        <div className="flex min-w-0 flex-1 items-center gap-2">{children}</div>
      </div>
    )}
  </AriaTreeItemContent>
);
TreeItemContent.displayName = 'TreeItemContent';

/**
 * Folder icon component that changes based on expanded state.
 * Use within TreeItemContent for folder items.
 */
interface FolderIconProps {
  className?: string;
}

const FolderIcon = ({
  className,
  ref,
}: FolderIconProps & {
  ref?: React.RefObject<SVGSVGElement>;
}) => (
  <span className={cn('@FolderIcon', 'relative')}>
    {/* Closed folder - visible when collapsed */}
    <Folder
      ref={ref}
      className={cn(
        'size-4 text-primary/50 transition-opacity duration-200',
        'group-data-[expanded]:opacity-0',
        className,
      )}
    />
    {/* Open folder - visible when expanded */}
    <FolderOpen
      className={cn(
        'absolute inset-0 size-4 text-primary/50 transition-opacity duration-200',
        'opacity-0 group-data-[expanded]:opacity-100',
        className,
      )}
    />
  </span>
);
FolderIcon.displayName = 'FolderIcon';

/**
 * Text content for tree items with proper truncation.
 */
const TreeItemText = ({
  className,
  children,
  ref,
  ...props
}: React.HTMLProps<HTMLSpanElement> & {
  ref?: React.RefObject<HTMLSpanElement>;
}) => (
  <span
    ref={ref}
    className={cn(
      '@TreeItemText',
      // Typography
      'text-sm font-medium',
      // Truncation
      'truncate',
      // Colors
      'text-foreground',
      'group-hover:text-foreground',
      'group-data-[selected]:text-space',
      className,
    )}
    {...props}
  >
    {children}
  </span>
);
TreeItemText.displayName = 'TreeItemText';

export { Tree, TreeItem, TreeItemContent, TreeItemText, FolderIcon };
